import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { HttpService } from '../core/http.service';
import { WebviewService } from '../platform/webview.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { Language } from './settings.service';

// Loose alias used at the fetch/seed seams. The public `languageJSON` field stays
// permissive so the app can keep reading keys with dot access (`languageJSON.Some_Key`)
// under `noPropertyAccessFromIndexSignature`.
export type LanguageJSON = Record<string, string>;

@Injectable({
    providedIn: 'root'
})
export class LanguageService {

    /**
     * Backed by a signal so that templates reading languageService.languageJSON.Some_Key
     * become consumers of it: loading a new language re-renders them with no manual
     * change detection. The property shape is unchanged for callers.
     */
    private readonly _languageJSON = signal<any>({});

    public get languageJSON(): any {
        return this._languageJSON();
    }
    public set languageJSON(json: any) {
        this._languageJSON.set(json);
    }

    /** Emits the currently loaded language code (null before the first load). */
    public readonly activeLanguage$ = new BehaviorSubject<string | null>(null);

    /** Signal-backed: templates read it through getLanguage(). */
    private readonly currentCode = signal<string>('');
    private readonly requested$ = new Subject<string>();

    private readonly httpService = inject(HttpService);
    private readonly webViewService = inject(WebviewService);
    private readonly consoleLoggerService = inject(ConsoleLoggerService);

    constructor() {
        this.requested$.pipe(
            distinctUntilChanged(),
            switchMap((code) => this.httpService.getLanguageJSON(code).pipe(
                map((json) => ({ code, json: json as LanguageJSON })),
                catchError((error) => {
                    this.consoleLoggerService.error('Failed to load language JSON', error);
                    return EMPTY; // keep the last successfully loaded language
                })
            )),
            takeUntilDestroyed()
        ).subscribe(({ code, json }) => this.apply(code, json));
    }

    /** Runtime language switch: persists, notifies the webview and fetches the new JSON. */
    public setLanguage(code: string): void {
        if (code === this.currentCode()) return;
        this.currentCode.set(code);
        localStorage.setItem('language', code);
        this.webViewService.SetUserLanguage(code);
        this.requested$.next(code);
    }

    /** Startup seed from the resolver's already-fetched JSON (no refetch). */
    public initLanguage(code: string, json: LanguageJSON): void {
        this.currentCode.set(code);
        this.apply(code, json);
    }

    public getLanguage(): string {
        return this.currentCode();
    }

    public getLanguageList(): { code: string, name: string }[] {
        return Language;
    }

    private apply(code: string, json: LanguageJSON): void {
        this.languageJSON = json;
        this.activeLanguage$.next(code);
    }

    public onlyEnglish = {
        "VoiceInputInfoDialog_Title": "Voice Instructions",
        "VoiceInputInfoDialog_Description": "Voice instructions gives you the freedom to set parameters or give command hand-free. After pressing the <img class='voice-icon' src='assets/icons/dark/ui/voice_recording_icon.svg'> button try these commands:",
        "Tutorial_Step_4_Command_SkipWaypoint": "Skip waypoint",
        "Tutorial_Step_4_Command_SkipChargingStation": "Skip charging station",
        "Tutorial_Step_4_Command_StopNavigation": "Stop navigation",
        "Tutorial_Step_4_Command_NumberOfPersons": "Number of persons x",
        "Tutorial_Step_4_Command_NavigateHome": "Navigate to home",
        "Tutorial_Step_4_Command_NavigateWork": "Navigate to work",
        "Tutorial_Step_4_Command_RemainingDistance": "Remaining distance",
        "Tutorial_Step_4_Command_ETE": "ETE",
        "Tutorial_Step_4_Command_BatteryLevel": "Set Battery level",
        "SUBS_ERR_001": "The subscription id field is required.",
        "SUBS_ERR_002": "The selected old vehicle id is required.",
        "SUBS_ERR_003": "The selected old vehicle id is invalid.",
        "SUBS_ERR_004": "The selected new vehicle id is required.",
        "SUBS_ERR_005": "The selected new vehicle id is invalid.",
        "SUBS_ERR_006": "The selected vehicle id is required.",
        "SUBS_ERR_007": "The selected user vehicle id is required."
    }
}
